import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { Search, Person, FilterList } from '@mui/icons-material';
import axios from 'axios';
import { api } from "../context/AuthContext";

function SuperAdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [activity, setActivity] = useState({ jobs: [], applications: [] });
  const [activityLoading, setActivityLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  useEffect(() => {
    async function fetchAdmins() {
      try {
        const response = await api.get('/super-admin/admins');
        setAdmins(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch admins:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAdmins();
  }, []);

  const handleViewActivity = async (admin) => {
    setSelectedAdmin(admin);
    setActivityLoading(true);
    setOpenDialog(true);
    setTabValue(0);
    try {
      const response = await api.get(`/super-admin/admins/${admin.id}/activity`);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = (admin.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.last_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.phone?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = subscriptionFilter === 'All' ||
        (subscriptionFilter === 'Subscribed' && admin.has_active_subscription) ||
        (subscriptionFilter === 'No Subscription' && !admin.has_active_subscription);

    return matchesSearch && matchesFilter;
  });

  const paginatedAdmins = filteredAdmins.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredAdmins.length / 10);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          {/*<Typography variant="h2" sx={{ color: "#1E3A8A", fontWeight: "bold" }}>*/}
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
          >
            Admins Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            View and manage all recruiters (admins) in the system.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search admins..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small">
            <Select
              value={subscriptionFilter}
              onChange={(e) => {
                setSubscriptionFilter(e.target.value);
                setPage(1);
              }}
              displayEmpty
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                  <FilterList sx={{ fontSize: 20 }} />
                  Filters
                </Box>
              )}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E3A8A',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E40AF',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                Filters
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Subscribed">Subscribed</MenuItem>
              <MenuItem value="No Subscription">No Subscription</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Admin</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subscription</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Joined Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAdmins.map((admin) => (
              <TableRow key={admin.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={admin.profile_image_url}
                      sx={{ bgcolor: 'primary.main' }}
                    >
                      {admin.first_name?.[0]}{admin.last_name?.[0]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {admin.first_name} {admin.last_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.phone || 'N/A'}</TableCell>
                <TableCell>{admin.company_name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={admin.has_active_subscription ? 'Subscribed' : 'No Subscription'}
                    color={admin.has_active_subscription ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {new Date(admin.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => handleViewActivity(admin)}
                  >
                    Activity
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredAdmins.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No admin users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
      >
        <Typography variant="body2" color="text.secondary">
          {paginatedAdmins.length > 0 ? (page - 1) * 10 + 1 : 0}–
          {Math.min(page * 10, filteredAdmins.length)} of{" "}
          {filteredAdmins.length} records
        </Typography>
        <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
        />
      </Box>

      {/* Activity Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Activity for {selectedAdmin?.first_name} {selectedAdmin?.last_name}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label={`Jobs (${activity.jobs.length})`} />
              <Tab label={`Applications (${activity.applications.length})`} />
            </Tabs>
          </Box>

          {activityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {tabValue === 0 && (
                <List>
                  {activity.jobs.length === 0 ? (
                    <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">
                      No jobs posted by this admin.
                    </Typography>
                  ) : (
                    activity.jobs.map((job) => (
                      <Paper key={job.id} variant="outlined" sx={{ mb: 1, p: 2, borderRadius: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {job.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Type: {job.job_type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Posted: {new Date(job.created_at).toLocaleDateString()}
                          </Typography>
                          <Chip 
                            label={job.is_active ? 'Active' : 'Inactive'} 
                            size="small" 
                            color={job.is_active ? 'success' : 'default'} 
                          />
                        </Box>
                      </Paper>
                    ))
                  )}
                </List>
              )}

              {tabValue === 1 && (
                <List>
                  {activity.applications.length === 0 ? (
                    <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">
                      No applications received for this admin's jobs.
                    </Typography>
                  ) : (
                    activity.applications.map((app) => (
                      <Paper key={app.id} variant="outlined" sx={{ mb: 1, p: 2, borderRadius: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {app.first_name} {app.last_name}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          Job: {app.job_title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Applied: {new Date(app.created_at).toLocaleDateString()}
                          </Typography>
                          <Chip 
                            label={app.status} 
                            size="small" 
                            color={app.status === 'hired' ? 'success' : 'primary'} 
                          />
                        </Box>
                      </Paper>
                    ))
                  )}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SuperAdminAdmins;
